// @ts-nocheck
import React, { useState } from 'react';

interface FeedItem {
  _id: string;
  title: string;
  feedUrl: string;
  siteUrl?: string;
  status: 'active' | 'paused' | 'error';
  unreadCount?: number;
  lastSuccessfulFetchAt?: string;
  lastError?: string;
  consecutiveFailures?: number;
}

interface ManageFeedsModalProps {
  isOpen: boolean;
  onClose: () => void;
  feeds: FeedItem[];
  onRefreshFeed: (feedId: string) => Promise<any>;
  onToggleStatus: (feed: FeedItem) => Promise<any>;
  onDeleteFeed: (feedId: string) => Promise<any>;
  onUpdateTitle: (feedId: string, title: string) => Promise<any>;
}

export default function ManageFeedsModal({
  isOpen,
  onClose,
  feeds = [],
  onRefreshFeed,
  onToggleStatus,
  onDeleteFeed,
  onUpdateTitle,
}: ManageFeedsModalProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [refreshingId, setRefreshingId] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleStartEdit = (feed: FeedItem) => {
    setEditingId(feed._id);
    setEditingTitle(feed.title);
  };

  const handleSaveEdit = async (feedId: string) => {
    if (editingTitle.trim()) {
      await onUpdateTitle(feedId, editingTitle.trim());
    }
    setEditingId(null);
  };

  const handleRefresh = async (feedId: string) => {
    setRefreshingId(feedId);
    try {
      await onRefreshFeed(feedId);
    } finally {
      setRefreshingId(null);
    }
  };

  return (
    <div className="reader-modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="reader-modal-card" style={{ maxWidth: '640px' }} onClick={(e) => e.stopPropagation()}>
        <div className="reader-modal-header">
          <h3 className="reader-modal-title">Manage Subscribed Feeds ({feeds.length})</h3>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--reader-text-muted)' }}
          >
            ×
          </button>
        </div>

        <div className="reader-modal-body" style={{ maxHeight: '70vh', padding: 0 }}>
          {feeds.length === 0 ? (
            <div className="reader-empty-state">
              <i className="fi fi-rr-rss reader-empty-icon" />
              <div className="reader-empty-title">No feed subscriptions</div>
              <div className="reader-empty-subtitle">
                Subscribe to blogs, news outlets, and podcasts to see them managed here.
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {feeds.map((feed) => (
                <div
                  key={feed._id}
                  style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid var(--reader-border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    {editingId === feed._id ? (
                      <div style={{ display: 'flex', gap: '6px', marginBottom: '4px' }}>
                        <input
                          type="text"
                          className="reader-input"
                          style={{ height: '30px', fontSize: '0.85rem' }}
                          value={editingTitle}
                          onChange={(e) => setEditingTitle(e.target.value)}
                          autoFocus
                        />
                        <button
                          type="button"
                          className="reader-btn reader-btn-primary"
                          style={{ height: '30px', padding: '0 8px' }}
                          onClick={() => handleSaveEdit(feed._id)}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="reader-btn"
                          style={{ height: '30px', padding: '0 8px' }}
                          onClick={() => setEditingId(null)}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{feed.title}</span>
                        <button
                          onClick={() => handleStartEdit(feed)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--reader-text-subtle)', padding: 0 }}
                          title="Rename feed"
                        >
                          <i className="fi fi-rr-edit" style={{ fontSize: '0.75rem' }} />
                        </button>
                        <span
                          style={{
                            fontSize: '0.68rem',
                            padding: '1px 6px',
                            borderRadius: '3px',
                            textTransform: 'uppercase',
                            fontWeight: 600,
                            backgroundColor:
                              feed.status === 'active'
                                ? 'rgba(34, 197, 94, 0.15)'
                                : feed.status === 'paused'
                                ? 'rgba(234, 179, 8, 0.15)'
                                : 'rgba(239, 68, 68, 0.15)',
                            color:
                              feed.status === 'active'
                                ? '#16a34a'
                                : feed.status === 'paused'
                                ? '#ca8a04'
                                : '#dc2626',
                          }}
                        >
                          {feed.status}
                        </span>
                      </div>
                    )}

                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: 'var(--reader-text-subtle)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {feed.feedUrl}
                    </div>

                    {feed.lastError && (
                      <div style={{ fontSize: '0.72rem', color: '#ef4444', marginTop: '2px' }}>
                        Error: {feed.lastError}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <button
                      className="reader-btn-icon"
                      onClick={() => handleRefresh(feed._id)}
                      disabled={refreshingId === feed._id}
                      title="Refresh feed now"
                    >
                      <i
                        className="fi fi-rr-refresh"
                        style={{ animation: refreshingId === feed._id ? 'spin 1s linear infinite' : 'none' }}
                      />
                    </button>

                    <button
                      className="reader-btn-icon"
                      onClick={() => onToggleStatus(feed)}
                      title={feed.status === 'paused' ? 'Resume feed' : 'Pause feed'}
                    >
                      <i className={`fi fi-rr-${feed.status === 'paused' ? 'play' : 'pause'}`} />
                    </button>

                    <button
                      className="reader-btn-icon"
                      onClick={() => onDeleteFeed(feed._id)}
                      title="Unsubscribe / delete feed"
                      style={{ color: '#ef4444' }}
                    >
                      <i className="fi fi-rr-trash" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="reader-modal-footer">
          <button type="button" className="reader-btn" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
